/* eslint-disable no-undef, no-unused-vars */
import { observer } from "mobx-react-lite";
import { useStore } from "../../../root-store-context";
import * as React from "react";
import { InboxOutlined } from "@ant-design/icons";
import type { PopconfirmProps, UploadProps } from "antd";
import { App, Button, Checkbox, Flex, Popconfirm, Spin, Typography, Upload } from "antd";
import { UploadGeneralList } from "./UploadGeneralList";
import { RcFile } from "antd/es/upload";
import {
  CamFileInfo,
  MailJobFile,
} from "../../../stores/AppStore/ApplicationStore";
import { UploadList } from "./UploadList";
import { useEffect } from "react";
import { useCamIsMobile } from "hooks/useCamIsMobile";

const { Dragger } = Upload;
const { Text } = Typography;

export type DataTypeTableUpload = {
  key: React.Key;
  keyData: React.Key;
  fileName: string;
  fileInfo: CamFileInfo;
  file: RcFile;
};

const popconfirmOkStyle: React.CSSProperties = {
  background: "#dc2626",
  borderColor: "#dc2626",
  color: "#ffffff",
  fontWeight: 600,
  borderRadius: 8,
  boxShadow: "0 2px 6px rgba(220,38,38,0.25)",
};

const popconfirmCancelStyle: React.CSSProperties = {
  fontWeight: 600,
  borderRadius: 8,
  color: "#315780",
  borderColor: "#cbd5e1",
  background: "#ffffff",
};

export const UploadApp = observer(() => {
  const { applicationStore } = useStore();
  const { message } = App.useApp();
  const isMobile = useCamIsMobile();

  const warning = (fileName: string) => {
    message.warning(`A file named "${fileName}" already exists.`);
  };

  const propertyIsAccess = () => {
    message.warning("You don't have permission. Please choose another property.");
  };

  const props: UploadProps = {
    name: "fileCam",
    multiple: true,
    showUploadList: false,
    beforeUpload: (file: RcFile): boolean => {
      if (
        !applicationStore.mailJob.files ||
        !applicationStore.mailJob.files.map((_) => _.name).includes(file.name)
      ) {
        applicationStore.uploadFile(file);
      } else {
        warning(file.name);
      }
      return false;
    },
  };

  const confirm: PopconfirmProps["onConfirm"] = () => {
    applicationStore.mailJob.files?.forEach((_) => {
      if (applicationStore.selectedFiles.includes(_.guid)) {
        message.warning(`Document "${_.name}" has been removed from your mailing.`);
      }
    });
    applicationStore.deleteFiles();
  };

  useEffect(() => {
    if (applicationStore.isAccessProperty) {
      propertyIsAccess();
    }
  }, [applicationStore.isAccessProperty]);

  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    document.addEventListener("dragover", prevent, true);
    document.addEventListener("drop", prevent, true);
    return () => {
      document.removeEventListener("dragover", prevent, true);
      document.removeEventListener("drop", prevent, true);
    };
  }, []);

  const isGeneralCorrespondence = applicationStore.communicationType.comTypeId === 1;
  const hasFiles = applicationStore.mailJob.files?.length > 0;
  const allGuids = applicationStore.mailJob.files?.map((_) => _.guid) ?? [];
  const allSelected = allGuids.length > 0 && allGuids.every((g) =>
    applicationStore.selectedFiles.includes(g)
  );
  const toggleSelectAll = () => {
    if (allSelected) {
      applicationStore.setSelectedFiles([]);
    } else {
      applicationStore.setSelectedFiles(allGuids);
    }
  };

  return (
    <Spin tip="Uploading..." spinning={applicationStore.isUploading}>
      <Flex gap="middle" vertical>

        <Dragger data-testid="cam-upload-dragger" {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop files here</p>
          <p className="ant-upload-hint">or click to select files</p>
        </Dragger>

        {hasFiles && (
          <>
            {isMobile && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                border: "1px dashed #cbd8ea",
                borderRadius: 8,
                background: "#ffffff",
              }}>
                <Checkbox
                  data-testid="cam-select-all-checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                >
                  <Text strong>Select All</Text>
                </Checkbox>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text strong>{isGeneralCorrespondence ? "Processing order" : "Documents"}</Text>
              {isGeneralCorrespondence && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  — drag to reorder
                </Text>
              )}
            </div>

            {isGeneralCorrespondence ? (
              <UploadGeneralList />
            ) : (
              <UploadList />
            )}

            <div className="cam__upload-actions">
              <Popconfirm
                title="Confirmation"
                description="Are you sure you want to remove the selected documents?"
                onConfirm={confirm}
                okText="Yes"
                cancelText="No"
                disabled={applicationStore.selectedFiles.length === 0}
                okButtonProps={{ style: popconfirmOkStyle }}
                cancelButtonProps={{ style: popconfirmCancelStyle }}
              >
                <Button
                  data-testid="cam-delete-selected-btn"
                  className="cam__button-danger-sm"
                  type="primary"
                  danger
                  disabled={applicationStore.selectedFiles.length === 0}
                >
                  Delete Selected
                </Button>
              </Popconfirm>
            </div>
          </>
        )}
      </Flex>
    </Spin>
  );
});